import { combineReducers } from "redux";
import * as actions from "../actions";
import _ from "lodash";
import {
  maskingPhoneNumber,
  numberWithCommas,
  phoneNumberFormat,
  replaceAt,
} from "../helpers/Utils";
import {
  OPERATION_LABEL,
  OPERATION_LABEL_FOR_DATE,
  OPERATION_LABEL_FOR_LOCATION,
  ORIGIN_LABELS,
  formatRecentPeriodValue,
} from "../helpers/GuideData";
import moment from "moment";
import Constants from "../helpers/Constants";

const initialState = {
  reportStatus: {
    isFetching: false,
    error: null,
  },
  report: {},
  customerStatus: {
    isFetching: false,
    error: null,
    action: null,
  },
  customers: {
    byId: {},
    count: 0,
  },
  customersByTag: {
    byId: {},
    count: 0,
  },
  groupStatus: {
    isFetching: false,
    error: null,
  },
  groups: [],
  customersStats: {
    byId: {},
    count: [],
  },
  tagStatus: {
    action: null,
    isFetching: false,
    error: null,
  },
  tagsById: {},
  retrieved: null,
};

function groupStatus(state = initialState.groupStatus, action) {
  switch (action.type) {
    case actions.GET_CUSTOMER_GROUPS:
    case actions.GET_CUSTOMER_GROUPS_WITH_COUNT:
    case actions.CREATE_OR_UPDATE_CUSTOMER_GROUP:
    case actions.DELETE_CUSTOMER_GROUP:
      return {
        isFetching: true,
        error: null,
      };
    case actions.GET_CUSTOMER_GROUPS_SUCCESS:
      return initialState.groupStatus;
    case actions.GET_CUSTOMER_GROUPS_FAILURE:
      return {
        isFetching: false,
        error: action.error,
      };
    default:
      return state;
  }
}

const groups = (state = initialState.groups, action) => {
  switch (action.type) {
    case actions.RECEIVE_CUSTOMER_GROUPS:
      return action.groups;
    case actions.SIGN_OUT:
      return initialState.groups;
    default:
      return state;
  }
};

function tagStatus(state = initialState.tagStatus, action) {
  switch (action.type) {
    case actions.GET_CUSTOMER_TAGS:
    case actions.GET_CUSTOMER_TAGS_WITH_COUNT:
    case actions.GET_CUSTOMERS_BY_TAG:
    case actions.CREATE_OR_UPDATE_CUSTOMER_TAG:
    case actions.UPDATE_CUSTOMER_TAGS:
    case actions.DELETE_CUSTOMER_TAG:
      return {
        action: action.type,
        isFetching: true,
        error: null,
      };
    case actions.SET_CUSTOMER_TAG_STATUS_SUCCESS:
      return initialState.tagStatus;
    case actions.SET_CUSTOMER_TAG_STATUS_FAILURE:
      return {
        action: action.type,
        isFetching: false,
        error: action.error,
      };
    default:
      return state;
  }
}

const tagsById = (state = initialState.tagsById, action) => {
  switch (action.type) {
    case actions.RECEIVE_CUSTOMER_TAGS:
      return action.tags.reduce(
        (obj, tag) => {
          obj[tag.id] = tag;
          return obj;
        },
        action.override ? {} : _.cloneDeep(state)
      );
    case actions.RECEIVE_CUSTOMER_TAG:
      if (action.tag && action.tag.id) {
        return { ..._.cloneDeep(state), [action.tag.id]: action.tag };
      }
      return state;
    case actions.RECEIVE_DELETED_CUSTOMER_TAG:
      const { [action.tagId]: _tag, ...tags } = state;
      return tags;
    case actions.SIGN_OUT:
      return initialState.tagsById;
    default:
      return state;
  }
};

function customerStatus(state = initialState.customerStatus, action) {
  switch (action.type) {
    case actions.GET_CUSTOMERS:
    case actions.GET_PROVIDER_CUSTOMER:
    case actions.GET_CORPORATE_CUSTOMERS:
    case actions.GET_CUSTOMER:
    case actions.CREATE_CUSTOMER:
    case actions.UPDATE_CUSTOMER:
    case actions.DELETE_CUSTOMER:
    case actions.GET_CUSTOMERS_STATS:
    case actions.GET_REGISTERED_CUSTOMER_COUNT:
    case actions.RETRIEVE_CUSTOMER:
    case actions.RETRIEVE_CUSTOMER_WITH_POINT:
    case actions.RETRIEVE_CUSTOMER_WITH_ORDER:
    case actions.RETRIEVE_CUSTOMER_WITH_CREDIT:
    case actions.RETRIEVE_CUSTOMER_WITH_COUPON:
    case actions.SEARCH_PROVIDER_CUSTOMER:
      return {
        isFetching: true,
        error: null,
        action: action.type,
      };
    case actions.GET_CUSTOMERS_SUCCESS:
    case actions.GET_CUSTOMER_SUCCESS:
    case actions.CREATE_CUSTOMER_SUCCESS:
    case actions.UPDATE_CUSTOMER_SUCCESS:
    case actions.DELETE_CUSTOMER_SUCCESS:
    case actions.GET_CUSTOMERS_STATS_SUCCESS:
    case actions.GET_REGISTERED_CUSTOMER_COUNT_SUCCESS:
      return initialState.customerStatus;
    case actions.GET_CUSTOMERS_FAILURE:
    case actions.GET_CUSTOMER_FAILURE:
    case actions.CREATE_CUSTOMER_FAILURE:
    case actions.UPDATE_CUSTOMER_FAILURE:
    case actions.DELETE_CUSTOMER_FAILURE:
    case actions.GET_CUSTOMERS_STATS_FAILURE:
    case actions.GET_REGISTERED_CUSTOMER_COUNT_FAILURE:
      return {
        isFetching: false,
        error: action.error,
        action: null,
      };
    default:
      return state;
  }
}

const customers = (state = initialState.customers, action) => {
  let _customers = _.cloneDeep(state);
  switch (action.type) {
    case actions.RECEIVE_CUSTOMERS:
      _customers.byId = action.customers.reduce(
        (obj, customer) => {
          if (customer._delta === "DELETE") {
            delete obj[_customer.id];
          } else {
            obj[customer.id] = {
              ...customer.customer,
              ...customer,
            };
            delete obj[customer.id].customer;
          }
          return obj;
        },
        action.override ? {} : _.cloneDeep(state.byId)
      );
      _customers.count = action.count;
      return _customers;
    case actions.RECEIVE_CUSTOMER:
      if (action.customer && action.customer.id) {
        _customers.byId = {
          ..._customers.byId,
          [action.customer.id]: {
            ...(action.customer.customer || {}),
            ...action.customer,
          },
        };
        delete _customers.byId[action.customer.id].customer;
        return _customers;
      }
      return _customers;
    case actions.RECEIVE_PROVIDER_CUSTOMER:
      if (action.customer && action.customer.providerCustomerId) {
        _customers.byId = {
          ..._customers.byId,
          [action.customer.providerCustomerId]: {
            ...action.customer,
          },
        };
      }
      return _customers;
    case actions.RECEIVE_PROVIDER_CUSTOMERS:
      _customers.byId = action.customers.reduce(
        (obj, cus) => {
          obj[cus.providerCustomerId] = cus;
          return obj;
        },
        action.override ? {} : _.cloneDeep(state.byId)
      );
      return _customers;
    case actions.RECEIVE_DELETED_CUSTOMER:
      const { [action.customerId]: _customer, ...customers } = state.byId;
      return {
        byId: customers,
        count: state.count - 1,
      };
    case actions.SIGN_OUT:
    case actions.CLEAR_CUSTOMERS:
      return initialState.customers;
    default:
      return state;
  }
};

const customersByTag = (state = initialState.customersByTag, action) => {
  let _customers = _.cloneDeep(state);
  switch (action.type) {
    case actions.RECEIVE_CUSTOMERS_BY_TAG:
      _customers.byId = action.customers.reduce(
        (obj, customer) => {
          if (customer._delta === "DELETE") {
            delete obj[customer.id];
          } else {
            obj[customer.id] = {
              ...customer.customer,
              ...customer,
            };
            delete obj[customer.id].customer;
          }
          return obj;
        },
        action.override ? {} : _.cloneDeep(_customers.byId)
      );
      let customerTags = [];
      action.customers.forEach((customer) => {
        customerTags = [
          ...customerTags,
          ...(customer.tags || []).map((tag) => ({
            customerId: customer.id,
            tagId: tag.tagId,
          })),
        ];
      });
      _customers.count = action.count;
      return _customers;
    case actions.SIGN_OUT:
    case actions.CLEAR_CUSTOMERS_BY_TAG:
      return initialState.customersByTag;
    default:
      return state;
  }
};

const customersStats = (state = initialState.customersStats, action) => {
  /**
   * [{id: , phone: , lastVisitDate: }]
   */
  switch (action.type) {
    case actions.RECIEVE_CUSTOMERS_STATS:
      const customersById = (action.customers || []).reduce(
        (obj, customer) => {
          obj[customer.id] = customer;
          return obj;
        },
        action.override ? {} : _.cloneDeep(state.byId)
      );
      return {
        ...state,
        byId: customersById,
        count: action.count,
      };
    default:
      return state;
  }
};

const retrieved = (state = initialState.retrieved, action) => {
  switch (action.type) {
    case actions.RECEIVE_RETRIEVED_CUSTOMER:
      return action.customer;
    case actions.CLEAR_RETRIEVED_CUSTOMER:
      return initialState.retrieved;
    default:
      return state;
  }
};

export const getCustomerPhone = (
  phone,
  isOutsourced,
  openState,
  agreements
) => {
  switch (isOutsourced) {
    case true:
      return phoneNumberFormat(phone);
    case false:
    default:
      if (openState) {
        if (agreements?.eula && agreements?.privacy && agreements?.provider) {
          return phoneNumberFormat(phone);
        } else {
          return maskingPhoneNumber(phone);
        }
      } else {
        return maskingPhoneNumber(phone);
      }
  }
};

export const getCustomerName = (
  name,
  isOutsourced,
  isAnonymous,
  openState,
  agreements
) => {
  switch (isOutsourced || isAnonymous) {
    case true:
      return name || "";
    case false:
    default:
      if (openState) {
        if (agreements?.eula && agreements?.privacy && agreements?.provider) {
          return name || "-";
        } else {
          return replaceAt(name, 1, "*") || "-";
        }
      } else {
        return replaceAt(name, 1, "*") || "-";
      }
  }
};

export const getPointLabel = (type) => {
  switch (type) {
    case "use":
      return "사용";
    case "save":
      return "적립";
    case "expired":
      return "만료";
    default:
      return "";
  }
};

// 성별 라벨 — 도메인 정본(CustomerModal 성별 표기)을 공용화.
// Customer.gender 는 enum 이 아니라 자유 String 이라 정의값 외에는 null 을 돌려주고,
// "정보없음" 을 쓸지 항목을 빼버릴지는 호출부가 정한다.
export const getCustomerGenderLabel = (gender) => {
  switch (gender) {
    case "male":
      return "남성";
    case "female":
      return "여성";
    default:
      return null;
  }
};

export const isNumberTagCondition = (key) => {
  switch (key) {
    case "orderAmount":
    case "visitCount":
    case "refundAmount":
    case "paidAmount":
    case "productName":
    case "categoryName":
      return true;
    default:
      return false;
  }
};

export const isStringTagCondition = (key) => {
  switch (key) {
    case "productName":
    case "categoryName":
      return true;
    default:
      return false;
  }
};

export const getCustomerGroupLabel = (groupKey) => {
  switch (groupKey) {
    case "all":
      return "전체고객";
    case "registered":
      return "전체등록고객";
    case "anonymous":
      return "미등록고객";

    default:
      return "";
  }
};

export const getTagConditionLabel = (conditionKey) => {
  switch (conditionKey) {
    case "lastVisit":
      return "마지막 방문일이";
    case "orderAmount":
      return "결제금액이";
    case "visitCount":
      return "총 주문횟수가";
    case "tier":
      return "고객등급이";
    case "orderLocationId":
      return "방문지점이";
    case "brandId":
      return "방문브랜드가";
    case "orderCreatedAt":
      return "주문기간이";
    case "productId":
      return "구매제품이";
    case "skuId":
      return "구매 하위제품(옵션)이";
    case "origin":
      return "주문처가";
    case "productName":
      return "구매제품 이름이";
    case "categoryName":
      return "구매카테고리 이름이";
    case "refundAmount":
      return "환불금액이";
    case "couponId":
      return "사용 쿠폰이";
    case "ownedCouponId":
      return "보유 쿠폰이";
    default:
      return "";
  }
};

const recentPeriodToRange = (code) => {
  const m = /^(\d+)([WMY])$/.exec(code || "");
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = { W: "weeks", M: "months", Y: "years" }[m[2]];
  const end = moment();
  const start = moment().subtract(n, unit);
  return [start.toDate(), end.toDate()];
};

export const getTagFilters = (tag) => {
  let filters = [];
  for (let condition of tag.conditions || []) {
    if (condition.key === "visitCount") {
      filters = [
        ...filters,
        ...condition.operations.map((op) => ({
          key: "orderCount",
          value: op.value,
          operation: op.operation,
        })),
      ];
    } else if (condition.key === "orderLocationId") {
      filters = [
        ...filters,
        ...condition.operations.map((op) => ({
          key: "locationId",
          value: op.value,
          operation: op.operation,
        })),
      ];
    } else if (condition.key === "lastVisit" || condition.key === "orderCreatedAt") {
      const key = condition.key === "lastVisit" ? "lastVisitedAt" : condition.key;
      filters = [
        ...filters,
        ...condition.operations.map((op) => {
          if (op.operation === "recent") {
            return {
              key,
              value: recentPeriodToRange(op.value),
              operation: "between",
            };
          }
          return {
            key,
            value: (op.value || []).map((v, index) => {
              let date = new Date(v);
              if (index === 0) {
                date.setHours(0, 0, 0, 0);
                date.toISOString();
                return date;
              } else {
                date.setHours(23, 59, 59, 0);
                date.toISOString();
                return date;
              }
            }),
            operation: op.operation,
          };
        }),
      ];
    } else {
      filters = [
        ...filters,
        ...condition.operations.map((op) => ({
          key: condition.key,
          value: op.value,
          operation: op.operation,
        })),
      ];
    }
  }
  return filters;
};

export const getTagLabel = (
  condition,
  offlineLocationsById,
  productsById = {},
  couponsById,
  brandsById = {}
) => {
  const parseJsonValue = (rawValue) => {
    if (!_.isString(rawValue)) {
      return rawValue;
    }
    const trimmed = rawValue.trim();
    if (!trimmed || !/^[\[{]/.test(trimmed)) {
      return rawValue;
    }
    try {
      return JSON.parse(trimmed);
    } catch (err) {
      return rawValue;
    }
  };
  let label = "";
  let subLabels = [];
  label = getTagConditionLabel(condition.key);
  for (let op of condition.operations || []) {
    if (op.value && op.operation) {
      let isDate =
        condition.key === "lastVisit" || condition.key === "orderCreatedAt";
      let isLocation = condition.key === "orderLocationId";
      let isBrand = condition.key === "brandId";
      let isNumber = op.type === "number";
      let isJson = op.type === "json";
      let value = isLocation
        ? offlineLocationsById[op.value]?.name
        : isNumber
        ? numberWithCommas(op.value)
        : isJson
        ? parseJsonValue(op.value)
        : op.value;
      if (condition.key === "productId") {
        value = productsById[value]?.name || "(삭제된 제품)";
      } else if (condition.key === "brandId") {
        value = brandsById[value]?.name || "선택 브랜드";
      } else if (
        condition.key === "couponId" ||
        condition.key === "ownedCouponId"
      ) {
        value = couponsById[value]?.name || "특정 쿠폰";
      } else if (condition.key === "origin") {
        value = ORIGIN_LABELS[value].label;
      } else if (condition.key === "orderCreatedAt") {
        if (op.operation === "recent") {
          value = formatRecentPeriodValue(op.value);
        } else {
          value = `${
            moment(value[0]).format(Constants.MOMENT.DATE_FORMAT) || ""
          } ~ ${moment(value[1]).format(Constants.MOMENT.DATE_FORMAT) || ""}`;
        }
      } else if (condition.key === "lastVisit") {
        if (op.operation === "recent") {
          value = formatRecentPeriodValue(op.value);
        } else if (_.isString(value)) {
          value = moment(value).format(Constants.MOMENT.DATE_FORMAT);
        } else {
          value = `${
            moment(value[0]).format(Constants.MOMENT.DATE_FORMAT) || ""
          } ~ ${moment(value[1]).format(Constants.MOMENT.DATE_FORMAT) || ""}`;
        }
      }
      subLabels = [
        ...subLabels,
        `${value} ${
          (isDate
            ? OPERATION_LABEL_FOR_DATE
            : isLocation || isBrand
            ? OPERATION_LABEL_FOR_LOCATION
            : OPERATION_LABEL)[op.operation]
        }`,
      ];
    }
  }
  return { label, subLabels };
};

export default combineReducers({
  customerStatus,
  customers,
  groups,
  groupStatus,
  tagsById,
  tagStatus,
  customersStats,
  customersByTag,
  retrieved,
});
